import React from "react";

import { Box, Chip, SxProps, Theme, Tooltip } from "@mui/material";

import { SignalStrengthMap } from "../../../../../constants/sensors";
import { SENSOR_CHIP_SX } from "components/sensors/table/chips/sensor-chip-style";
import { SENSOR_STATUS_COLOR } from "components/sensors/table/chips/sensor-status-color";
import {
  NO_VALUE_TEXT_SX,
  noValueText,
} from "components/sensors/table/no-value-text";

const PILL_WIDTH = "96px";

// Vendors report signal strength on wildly different raw scales (e.g.
// Monnit's "good" threshold is 69, MyDevices' is -90 dBm), so sorting by the
// raw signalStrength number doesn't correspond to sorting by the displayed
// tier at all -- a Monnit reading of 45 and a MyDevices reading of -95 can
// both be "Intermittent" despite being nowhere near each other numerically.
// This returns a tier rank shared by the sort valueGetter and the renderer,
// so sort order always matches what's on screen.
export const SIGNAL_TIER_RANK = {
  none: 0,
  poor: 1,
  intermittent: 2,
  good: 3,
  excellent: 4,
  // Signal readings don't apply for some sensors (e.g. weather sensors),
  // which show "N/A" on screen, not "No reading". CSV export needs to
  // tell the two apart.
  notApplicable: 5,
} as const;

export function signalTier(signalStrength: string, vendor: string) {
  if (!signalStrength) {
    return { rank: SIGNAL_TIER_RANK.none, label: null as string | null };
  }
  const signalValue = parseFloat(signalStrength);
  const signalMapper = SignalStrengthMap[vendor];
  if (!signalMapper) {
    return {
      rank: SIGNAL_TIER_RANK.notApplicable,
      label: null as string | null,
    };
  }
  if (isNaN(signalValue)) {
    return { rank: SIGNAL_TIER_RANK.none, label: null as string | null };
  }
  if (signalValue > signalMapper["good"]) {
    return { rank: SIGNAL_TIER_RANK.excellent, label: "Excellent" };
  }
  if (signalValue > signalMapper["intermittent"]) {
    return { rank: SIGNAL_TIER_RANK.good, label: "Good" };
  }
  if (signalValue > signalMapper["poor"]) {
    return { rank: SIGNAL_TIER_RANK.intermittent, label: "Intermittent" };
  }
  return { rank: SIGNAL_TIER_RANK.poor, label: "Poor" };
}

// Rank -> label lookup, for contexts (e.g. CSV export) that only have the
// sortable rank on hand, not the full signalTier() computation.
export const SIGNAL_TIER_LABEL: Record<number, string> = {
  [SIGNAL_TIER_RANK.excellent]: "Excellent",
  [SIGNAL_TIER_RANK.good]: "Good",
  [SIGNAL_TIER_RANK.intermittent]: "Intermittent",
  [SIGNAL_TIER_RANK.poor]: "Poor",
  [SIGNAL_TIER_RANK.notApplicable]: "N/A",
};

const SIGNAL_TIER_PILL_COLOR: Record<number, SxProps<Theme>> = {
  [SIGNAL_TIER_RANK.excellent]: SENSOR_STATUS_COLOR.positive,
  [SIGNAL_TIER_RANK.good]: SENSOR_STATUS_COLOR.good,
  [SIGNAL_TIER_RANK.intermittent]: SENSOR_STATUS_COLOR.caution,
  [SIGNAL_TIER_RANK.poor]: SENSOR_STATUS_COLOR.negative,
};

export function SignalStrengthChip({
  signalStrength,
  vendor,
}: {
  signalStrength: string;
  vendor: string;
}) {
  if (!signalStrength) {
    return noValueText("No reading");
  }

  const signalValue = parseFloat(signalStrength);
  const signalMapper = SignalStrengthMap[vendor];
  if (isNaN(signalValue)) {
    return noValueText("No reading");
  }
  if (!signalMapper) {
    // Not every vendor reports a signal band we know how to grade (e.g.
    // AccuWeather isn't an RF/cellular device) -- show N/A instead of
    // crashing on an undefined lookup. Plain text, not a pill, since this
    // isn't a status/reading value like the other states.
    return (
      <Tooltip title="Signal strength isn't tracked for this vendor">
        <Box component="span" sx={NO_VALUE_TEXT_SX}>
          N/A
        </Box>
      </Tooltip>
    );
  }

  const { rank, label } = signalTier(signalStrength, vendor);
  if (rank === SIGNAL_TIER_RANK.none || !label) {
    return noValueText("No reading");
  }

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        ...SENSOR_CHIP_SX,
        width: PILL_WIDTH,
        ...SIGNAL_TIER_PILL_COLOR[rank],
      }}
    />
  );
}
